import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MIcon } from '../../lib/micon';
import { PortalMenu } from '../../features/klasse/KlassenKopf';
import './ld-fenster.css';

// ── Welle K4: EIN Feld-Stil für alle Fenster ─────────────────────────────────────
// Rahmen (Kopf, Fuß) sitzt an LdSheet (kopf/fuss). Hier die Feld-Bausteine: weiß, 1-px-Rahmen,
// Radius 12, Höhe 44, Beschriftung darüber in normaler Schreibweise, „optional“ klein dahinter.
// LdAuswahl und LdDatum sind Ersatz für <select> und <input type="date"> mit demselben
// value/onChange-Vertrag (onChange({ target: { value } })) — die Logik der Fenster bleibt unverändert.

type Aenderung = { target: { value: string }; currentTarget: { value: string } };
const aenderung = (value: string): Aenderung => ({ target: { value }, currentTarget: { value } });

/** Beschriftung über einem Feld; optional = kleines „optional“ dahinter; rechts = z. B. Zeichenzahl */
export function LdFeld({ label, optional = false, rechts, htmlFor, hinweis, fehler, children, className }: {
  label?: React.ReactNode; optional?: boolean; rechts?: React.ReactNode; htmlFor?: string;
  hinweis?: React.ReactNode; fehler?: React.ReactNode; children: React.ReactNode; className?: string;
}) {
  return (
    <div className={`ld-fe-feld${className ? ` ${className}` : ''}`}>
      {(label || rechts) && (
        <div className="ld-fe-label">
          {label && <label htmlFor={htmlFor}>{label}{optional && <small>optional</small>}</label>}
          {rechts && <span className="ld-fe-label__rechts">{rechts}</span>}
        </div>
      )}
      {children}
      {hinweis && <p className="ld-fe-hinweis">{hinweis}</p>}
      {fehler && <p className="ld-fe-fehler" role="alert">{fehler}</p>}
    </div>
  );
}

/** Zwei Felder nebeneinander (Handy und PC) */
export function LdFeldReihe({ children }: { children: React.ReactNode }) {
  return <div className="ld-fe-reihe">{children}</div>;
}

/** Eingabe mit optionalem Element rechts im Feld (z. B. Mikro) */
export const LdEingabe = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement> & { rechts?: React.ReactNode }>(
  function LdEingabe({ rechts, className, style, ...rest }, ref) {
    return (
      <span className={`ld-fe-huelle${rechts ? ' mit-rechts' : ''}`} style={style}>
        <input ref={ref} className={`ld-fe-input${className ? ` ${className}` : ''}`} {...rest} />
        {rechts && <span className="ld-fe-rechts">{rechts}</span>}
      </span>
    );
  });

export const LdTextfeld = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement> & { rechts?: React.ReactNode }>(
  function LdTextfeld({ rechts, className, style, rows = 3, ...rest }, ref) {
    return (
      <span className={`ld-fe-huelle${rechts ? ' mit-rechts' : ''}`} style={style}>
        <textarea ref={ref} rows={rows} className={`ld-fe-input ld-fe-textarea${className ? ` ${className}` : ''}`} {...rest} />
        {rechts && <span className="ld-fe-rechts ld-fe-rechts--unten">{rechts}</span>}
      </span>
    );
  });

// ── Auswahl (statt <select>) ─────────────────────────────────────────────────────
type Option = { value: string; label: string; disabled: boolean };
function optionenAus(children: React.ReactNode): Option[] {
  const aus: Option[] = [];
  const lauf = (c: React.ReactNode) => React.Children.forEach(c, k => {
    if (!React.isValidElement(k)) return;
    const el = k as React.ReactElement<any>;
    if (el.type === React.Fragment || el.type === 'optgroup') { lauf(el.props.children); return; }
    if (el.type === 'option') {
      const text = React.Children.toArray(el.props.children).map(x => (typeof x === 'string' || typeof x === 'number' ? String(x) : '')).join('');
      aus.push({ value: String(el.props.value ?? text), label: text, disabled: !!el.props.disabled });
    }
  });
  lauf(children);
  return aus;
}

/**
 * Ersatz für <select>: Kinder sind weiter <option value=…>…</option>; onChange bekommt { target: { value } }.
 * Lange Listen scrollen im Menü.
 */
export function LdAuswahl({ value, onChange, children, disabled, id, placeholder, className, style, ...aria }: {
  value: string | number | null | undefined;
  onChange: (e: Aenderung) => void;
  children: React.ReactNode;
  disabled?: boolean; id?: string; placeholder?: string; className?: string; style?: React.CSSProperties;
  'aria-label'?: string; 'aria-labelledby'?: string;
}) {
  const optionen = useMemo(() => optionenAus(children), [children]);
  const [offen, setOffen] = useState(false);
  const knopf = useRef<HTMLButtonElement>(null);
  const wert = value == null ? '' : String(value);
  const gewaehlt = optionen.find(o => o.value === wert);
  return (
    <>
      <button ref={knopf} id={id} type="button" disabled={disabled} className={`ld-fe-input ld-fe-auswahl${className ? ` ${className}` : ''}`} style={style}
        aria-haspopup="listbox" aria-expanded={offen} aria-label={aria['aria-label']} aria-labelledby={aria['aria-labelledby']}
        onClick={() => setOffen(o => !o)}>
        <span className={gewaehlt && gewaehlt.value !== '' ? undefined : 'ist-leer'}>{gewaehlt?.label ?? placeholder ?? 'Bitte wählen'}</span>
        <MIcon name="expand_more" size={20} />
      </button>
      {offen && (
        <PortalMenu anker={knopf.current} links rolle="listbox" label={aria['aria-label'] ?? 'Auswahl'} onClose={() => setOffen(false)}>
          <div className="ld-fe-liste" style={{ minWidth: knopf.current?.offsetWidth }}>
            {optionen.map(o => (
              <button key={o.value} type="button" role="option" aria-selected={o.value === wert} disabled={o.disabled}
                className={`ld-kk-menu__item${o.value === wert ? ' ist-gewaehlt' : ''}`}
                onClick={() => { setOffen(false); if (o.value !== wert) onChange(aenderung(o.value)); }}>
                {o.value === wert ? <MIcon name="check" size={18} /> : <span style={{ width: 18, flexShrink: 0 }} />}{o.label}
              </button>
            ))}
          </div>
        </PortalMenu>
      )}
    </>
  );
}

// ── Datum (statt <input type="date">): Anzeige „Do, 22.10.2026“, Tippen „22.10.2026“, Kalender ──
const WT = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
const MONATE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
const istIso = (v: string) => /^\d{4}-\d{2}-\d{2}$/.test(v);
const ttmmjjjj = (v: string) => `${v.slice(8, 10)}.${v.slice(5, 7)}.${v.slice(0, 4)}`;
/** „Do, 22.10.2026“ */
export const datumLang = (v: string) => (istIso(v.slice(0, 10)) ? `${WT[new Date(v.slice(0, 10) + 'T12:00:00Z').getUTCDay()]}, ${ttmmjjjj(v.slice(0, 10))}` : v);
const zwei = (n: number) => String(n).padStart(2, '0');
function parseDatum(text: string): string | null {
  const t = text.trim();
  if (istIso(t)) return t;
  const m = /^(\d{1,2})\.(\d{1,2})\.?(\d{2}|\d{4})?$/.exec(t);
  if (!m) return null;
  const j = m[3] ? (m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3])) : new Date().getFullYear();
  const d = new Date(Date.UTC(j, Number(m[2]) - 1, Number(m[1]), 12));
  if (d.getUTCMonth() !== Number(m[2]) - 1) return null;
  return `${j}-${zwei(Number(m[2]))}-${zwei(Number(m[1]))}`;
}

/** Kalender als Popover am Anker (auch ohne Eingabefeld, z. B. Tagestitel im Klassenbuch) */
export function LdKalender({ anker, wert, min, max, leeren = false, onWahl, onClose }: {
  anker: HTMLElement | null; wert: string; min?: string; max?: string; leeren?: boolean;
  onWahl: (d: string) => void; onClose: () => void;
}) {
  const [monat, setMonat] = useState(() => (wert || new Date().toLocaleDateString('sv-SE')).slice(0, 7));
  const erlaubt = (d: string) => (!min || d >= min) && (!max || d <= max);
  const tage = (() => {
    const [j, m] = monat.split('-').map(Number);
    const erster = new Date(Date.UTC(j, m - 1, 1, 12));
    const versatz = (erster.getUTCDay() + 6) % 7;
    const anzahl = new Date(Date.UTC(j, m, 0, 12)).getUTCDate();
    return [...Array(versatz).fill(null), ...Array.from({ length: anzahl }, (_, i) => `${j}-${zwei(m)}-${zwei(i + 1)}`)];
  })();
  const blaettern = (n: number) => { const [j, m] = monat.split('-').map(Number); const d = new Date(Date.UTC(j, m - 1 + n, 1, 12)); setMonat(`${d.getUTCFullYear()}-${zwei(d.getUTCMonth() + 1)}`); };
  const heute = new Date().toLocaleDateString('sv-SE');
  return (
    <PortalMenu anker={anker} links rolle="dialog" label="Datum wählen" onClose={onClose}>
      <div className="ld-fe-kal">
        <div className="ld-fe-kal__kopf">
          <button type="button" aria-label="Voriger Monat" onClick={() => blaettern(-1)}><MIcon name="chevron_left" size={20} /></button>
          <b>{MONATE[Number(monat.slice(5, 7)) - 1]} {monat.slice(0, 4)}</b>
          <button type="button" aria-label="Nächster Monat" onClick={() => blaettern(1)}><MIcon name="chevron_right" size={20} /></button>
        </div>
        <div className="ld-fe-kal__gitter">
          {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map(t => <span key={t} className="ld-fe-kal__wt">{t}</span>)}
          {tage.map((d, i) => d ? (
            <button key={d} type="button" disabled={!erlaubt(d)} aria-pressed={d === wert} aria-label={datumLang(d)}
              className={`${d === wert ? 'ist-gewaehlt' : ''}${d === heute ? ' ist-heute' : ''}`}
              onClick={() => { onWahl(d); onClose(); }}>{Number(d.slice(8))}</button>
          ) : <span key={`l${i}`} />)}
        </div>
        <div className="ld-fe-kal__fuss">
          <button type="button" className="ld-kl-link" onClick={() => { if (erlaubt(heute)) { onWahl(heute); onClose(); } }}>Heute</button>
          {leeren && wert && <button type="button" className="ld-kl-link" style={{ color: 'var(--ld-text-secondary)' }} onClick={() => { onWahl(''); onClose(); }}>Leeren</button>}
        </div>
      </div>
    </PortalMenu>
  );
}

export function LdDatum({ value, onChange, min, max, disabled, id, placeholder, className, style, ...aria }: {
  value: string | null | undefined;
  onChange: (e: Aenderung) => void;
  min?: string; max?: string; disabled?: boolean; id?: string; placeholder?: string; className?: string; style?: React.CSSProperties;
  'aria-label'?: string; required?: boolean;
}) {
  const wert = value ? String(value).slice(0, 10) : '';
  const [fokus, setFokus] = useState(false);
  const [text, setText] = useState('');
  const [offen, setOffen] = useState(false);
  const knopf = useRef<HTMLButtonElement>(null);
  const huelle = useRef<HTMLSpanElement>(null);
  const erlaubt = (d: string) => (!min || d >= min) && (!max || d <= max);
  const setze = (d: string) => { if (d !== wert) onChange(aenderung(d)); };

  return (
    <span ref={huelle} className={`ld-fe-huelle mit-rechts${className ? ` ${className}` : ''}`} style={style}>
      <input id={id} className="ld-fe-input" type="text" inputMode="numeric" disabled={disabled} aria-label={aria['aria-label']}
        placeholder={fokus ? 'TT.MM.JJJJ' : (placeholder ?? 'Datum wählen')}
        value={fokus ? text : (wert ? datumLang(wert) : '')}
        onFocus={e => {
          // sofort umschalten und alles markieren — Tippen ersetzt dann das Datum statt anzuhängen
          const t = wert ? ttmmjjjj(wert) : '';
          e.currentTarget.value = t; e.currentTarget.select();
          setFokus(true); setText(t);
        }}
        onChange={e => setText(e.target.value)}
        onBlur={e => {
          setFokus(false);
          const roh = e.currentTarget.value;   // DOM-Wert (auch wenn das Feld ohne Fokus-Ereignis befüllt wurde)
          if (!roh.trim()) { if (wert && !aria.required) setze(''); return; }
          const d = parseDatum(roh);
          if (d && erlaubt(d)) setze(d);
        }}
        onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} />
      <button ref={knopf} type="button" className="ld-fe-rechts ld-fe-icon" disabled={disabled} aria-label="Kalender öffnen" aria-haspopup="dialog" aria-expanded={offen}
        onClick={() => setOffen(o => !o)}>
        <MIcon name="calendar_today" size={19} />
      </button>
      {offen && <LdKalender anker={huelle.current} wert={wert} min={min} max={max} leeren={!aria.required} onWahl={setze} onClose={() => setOffen(false)} />}
    </span>
  );
}

// ── Uhrzeit (optional): leer = ganztägig; Tippen „930“, „9:30“, „09.30“ ──
export function parseUhrzeit(text: string): string | null {
  const t = text.trim().replace('.', ':');
  if (!t) return '';
  const m = /^(\d{1,2})(?::?(\d{2}))?$/.exec(t);
  if (!m) return null;
  const h = Number(m[1]), min = Number(m[2] ?? 0);
  return h < 24 && min < 60 ? `${zwei(h)}:${zwei(min)}` : null;
}
/** kompakt = ohne Symbol rechts (Tabellen); leeren durch Löschen des Texts */
export function LdUhrzeit({ value, onChange, id, placeholder = 'ganztägig', kompakt = false, ...aria }: {
  value: string; onChange: (v: string) => void; id?: string; placeholder?: string; kompakt?: boolean; 'aria-label'?: string;
}) {
  const [text, setText] = useState(value);
  useEffect(() => { setText(value); }, [value]);
  return (
    <span className={`ld-fe-huelle${kompakt ? ' ist-kompakt' : ' mit-rechts'}`}>
      <input id={id} className="ld-fe-input" type="text" inputMode="numeric" placeholder={placeholder} aria-label={aria['aria-label']}
        value={text} onChange={e => setText(e.target.value)}
        onBlur={e => { const v = parseUhrzeit(e.currentTarget.value); if (v == null) setText(value); else { setText(v); if (v !== value) onChange(v); } }}
        onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} />
      {kompakt ? null : value
        ? <button type="button" className="ld-fe-rechts ld-fe-icon" aria-label="Uhrzeit entfernen (ganztägig)" onClick={() => onChange('')}><MIcon name="close" size={18} /></button>
        : <span className="ld-fe-rechts ld-fe-icon" aria-hidden="true"><MIcon name="schedule" size={19} /></span>}
    </span>
  );
}

/** Zahl mit neutralen Minus/Plus-Knöpfen */
export function LdZahl({ value, onChange, min = 0, max = 99, schritt = 1, ...aria }: {
  value: number; onChange: (n: number) => void; min?: number; max?: number; schritt?: number; 'aria-label'?: string;
}) {
  const label = aria['aria-label'] ?? 'Zahl';
  return (
    <span className="ld-fe-zahl" role="group" aria-label={label}>
      <button type="button" aria-label={`${label} weniger`} disabled={value <= min} onClick={() => onChange(Math.max(min, +(value - schritt).toFixed(2)))}><MIcon name="remove" size={20} /></button>
      <output aria-live="polite">{String(value).replace('.', ',')}</output>
      <button type="button" aria-label={`${label} mehr`} disabled={value >= max} onClick={() => onChange(Math.min(max, +(value + schritt).toFixed(2)))}><MIcon name="add" size={20} /></button>
    </span>
  );
}

/** Umschalter (Zwei- oder Dreierwahl), z. B. entschuldigt/unentschuldigt */
export function LdUmschalter<T extends string>({ optionen, wert, onChange, ...aria }: {
  optionen: { wert: T; label: React.ReactNode; ton?: 'amber' | 'rot' | 'gruen' }[]; wert: T; onChange: (w: T) => void; 'aria-label'?: string;
}) {
  return (
    <span className="ld-fe-umschalter" role="radiogroup" aria-label={aria['aria-label']}>
      {optionen.map(o => (
        <button key={o.wert} type="button" role="radio" aria-checked={o.wert === wert}
          className={`${o.wert === wert ? 'ist-an' : ''}${o.ton ? ` ton-${o.ton}` : ''}`} onClick={() => onChange(o.wert)}>{o.label}</button>
      ))}
    </span>
  );
}

/** Bis etwa 8 Optionen als Chips; punkt = Kategorie-Farbe als kleiner Punkt */
export function LdChips<T extends string | number>({ optionen, wert, onChange, ...aria }: {
  optionen: { wert: T; label: string; punkt?: string; icon?: string }[]; wert: T | null | undefined; onChange: (w: T) => void; 'aria-label'?: string;
}) {
  return (
    <span className="ld-fe-chips" role="radiogroup" aria-label={aria['aria-label']}>
      {optionen.map(o => (
        <button key={String(o.wert)} type="button" role="radio" aria-checked={o.wert === wert}
          className={`ld-fe-chip${o.wert === wert ? ' ist-an' : ''}`} onClick={() => onChange(o.wert)}>
          {o.punkt && <i style={{ background: o.punkt }} aria-hidden="true" />}{o.icon && <MIcon name={o.icon} size={16} />}{o.label}
        </button>
      ))}
    </span>
  );
}

/** KI-Hinweis als kleine Tint-Zeile mit Funken-Symbol */
export function LdKiHinweis({ children }: { children: React.ReactNode }) {
  return <div className="ld-fe-ki" role="status"><MIcon name="auto_awesome" size={18} />{children}</div>;
}

/** Schalter-Zeile (z. B. Anpinnen) */
export function LdSchalterZeile({ icon, titel, text, an, onChange }: {
  icon?: string; titel: string; text?: string; an: boolean; onChange: (an: boolean) => void;
}) {
  return (
    <button type="button" role="switch" aria-checked={an} className="ld-fe-schalter" onClick={() => onChange(!an)}>
      {icon && <MIcon name={icon} size={20} />}
      <span><b>{titel}</b>{text && <small>{text}</small>}</span>
      <span className={`ld-fe-schalter__knopf${an ? ' ist-an' : ''}`} aria-hidden="true"><i /></span>
    </button>
  );
}

/** Fußleiste: Abbrechen + eine Hauptaktion mit Verb; links optional ein Link (z. B. „Neu formulieren“) */
export function LdFensterFuss({ onAbbrechen, abbrechen = 'Abbrechen', haupt, onHaupt, aus = false, laden = false, links, gefahr = false }: {
  onAbbrechen: () => void; abbrechen?: string; haupt?: React.ReactNode; onHaupt?: () => void; aus?: boolean; laden?: boolean; links?: React.ReactNode; gefahr?: boolean;
}) {
  return (
    <>
      {links && <span className="ld-fe-fuss__links">{links}</span>}
      <button type="button" className="ld-fe-knopf ld-fe-knopf--zweit" onClick={onAbbrechen}>{abbrechen}</button>
      {haupt && (
        <button type="button" className={`ld-fe-knopf ld-fe-knopf--haupt${gefahr ? ' ist-gefahr' : ''}`} disabled={aus || laden} onClick={onHaupt}>
          {laden ? 'Bitte warten …' : haupt}
        </button>
      )}
    </>
  );
}
